/// <reference types="react-scripts" />
import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import { IFrontPageRecords } from "source/models/front/frontPageRecords";
import * as frontService from "source/models/services/frontService";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { PageDrawer } from "source/components/framework/Page/PageDrawer";
import { AppSettingsIcon } from "source/components/framework/Icon/AppSettingsIcon";
import { frontSetRecordsCompany, frontSetRecordsUser } from "source/models/store/front";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";

interface IModifiedRecordPropertiesDialogProp {
    title?: string;
    panelSize: number;
    panelVisible: boolean;
    records: IFrontPageRecords;
    userModified: boolean;
    onSavePanelSize: (panelSize: number, panelVisible: boolean) => void;
    onClose: () => void;
}

interface IModifiedRecordPropertiesDialogState {
    panelSize: string;
    panelVisible: boolean;
    recordCount: string;
    dateRange: string;
}

export const ModifiedRecordPropertiesDialog = (props: IModifiedRecordPropertiesDialogProp) => {
    const [state, setState] = useState<IModifiedRecordPropertiesDialogState>({
        panelSize: props.panelSize.toString(10),
        panelVisible: props.panelVisible,
        recordCount: props.records
            ? (props.userModified
                ? props.records.userRecordCount
                : props.records.companyRecordCount).toString(10)
            : "10",
        dateRange: props.records
            ? (props.userModified
                ? props.records.userDateRange
                : props.records.companyDateRange).toString(10)
            : "0"
    });

    const getSaveDataFromState = useCallback((saveState: IModifiedRecordPropertiesDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("userModified", props.userModified);
        result.append("recordCount", saveState.recordCount.toInteger());
        result.append("dateRange", saveState.dateRange.toInteger());
        //Only for change detection - data is saved with separate operation
        result.append("panelSize", saveState.panelSize.toInteger());
        result.append("panelVisible", saveState.panelVisible);
        return result;
    }, [props.userModified]);

    const orgStateHash = useRef<string>(getSaveDataFromState(state).hash);

    const changePanelVisibility = (panelVisible: boolean) => {
        setState((old) => ({ ...old, panelVisible }));
    };

    const saveToDb = async () => {
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => frontService.saveModifiedRecords(saveData.obj), false);
        if (!result) return;
        //Set app state
        if (props.userModified) {
            store.customStore.dispatch(frontSetRecordsUser(state.recordCount.toInteger(), state.dateRange.toInteger()));
        } else {
            store.customStore.dispatch(frontSetRecordsCompany(state.recordCount.toInteger(), state.dateRange.toInteger()));
        }
        //Close dialog
        props.onClose();
        //Save panel size
        props.onSavePanelSize(state.panelSize.toInteger(), state.panelVisible);
    };

    const handleSave = () => {
        saveToDb();
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            saveToDb();
        } else {
            props.onClose();
        }
    };

    return (
        <PageDrawer
            open
            icon={<AppSettingsIcon />}
            title={props.title}
            parentTitle={Translations.Panels}
            onClose={handleCancel}
        >
            <Box display="flex" flexDirection="column" flexGrow={2} margin="24px">
                <Box display="flex" flexDirection="column" flexGrow={2}>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormControlLabel
                                label={Translations.PanelVisible}
                                control={
                                    <Checkbox
                                        checked={state.panelVisible}
                                        onChange={(_e, checked) => changePanelVisibility(checked)}
                                    />
                                }
                            />
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.PanelSize}>
                                <IdTitleDropdown
                                    selectedId={state.panelSize}
                                    items={[
                                        { id: "1", title: Translations.PanelSize1 },
                                        { id: "2", title: Translations.PanelSize2 },
                                        { id: "5", title: Translations.PanelSize5 },
                                        { id: "3", title: Translations.PanelSize3 },
                                        { id: "4", title: Translations.PanelSize4 },
                                        { id: "6", title: Translations.PanelSize6 }
                                    ]}
                                    onChange={(panelSize) => setState((old) => ({ ...old, panelSize }))}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.ChangesMaxAmount}>
                                <IdTitleDropdown
                                    selectedId={state.recordCount}
                                    items={[
                                        { id: "10", title: "10" },
                                        { id: "20", title: "20" },
                                        { id: "30", title: "30" },
                                    ]}
                                    onChange={(recordCount) => setState((old) => ({ ...old, recordCount }))}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.OldestModification}>
                                <IdTitleDropdown
                                    selectedId={state.dateRange}
                                    items={[
                                        { id: "0", title: Translations.DaysAgo30 },
                                        { id: "1", title: Translations.DaysAgo60 },
                                        { id: "2", title: Translations.DaysAgo90 },
                                        { id: "3", title: Translations.DaysAgo120 },
                                    ]}
                                    onChange={(dateRange) => setState((old) => ({ ...old, dateRange }))}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </Box>
                <FilterButtons
                    chartPanelPropertiesMode
                    onCancel={handleCancel}
                    onSave={handleSave}
                />
            </Box>
        </PageDrawer>
    );
};